import { zodResolver } from '@hookform/resolvers/zod';
import { SessionUser } from '@certhon/models';
import { Loader2, MailWarning } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useIsOnline } from '@/features/offline/useSyncStatus';
import useAuthService from '@/hooks/use-auth-service';
import { ProfileUpdateError } from '@/services/AuthService';
import { EmailFormValues, emailSchema } from '../settings.schemas';

/**
 * Email change for the signed-in user. The auth service mails a
 * verification link to the new address; the change only takes effect once
 * that link (`/confirm-email`) has been opened.
 */
export function EmailChangeForm({ user }: { user: SessionUser }) {
  const authService = useAuthService();
  const isOnline = useIsOnline();

  const form = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: user.email ?? '' },
  });

  // A profile refresh (confirmation on another device, token renewal) resets
  // the untouched form to the current address.
  useEffect(() => {
    if (!form.formState.isDirty) {
      form.reset({ email: user.email ?? '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.email]);

  const onSubmit = async (values: EmailFormValues) => {
    if (values.email === user.email) {
      form.reset(values);
      return;
    }
    try {
      await authService.requestEmailChange(values.email);
      form.reset({ email: user.email ?? '' });
      toast.success('Verification mail sent', {
        description: `Open the link we sent to ${values.email} to start using it.`,
      });
    } catch (error) {
      if (error instanceof ProfileUpdateError && error.status === 400) {
        form.setError('email', { message: 'Enter a valid email address' });
        return;
      }
      toast.error('Something went wrong', {
        description:
          'Your email address has not been changed. Please try again later.',
      });
    }
  };

  const { isSubmitting, isDirty } = form.formState;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {user.email_new && (
          <Alert>
            <MailWarning className="h-4 w-4" />
            <AlertDescription>
              A verification mail has been sent to <b>{user.email_new}</b>. Your
              address changes once you open the link in it.
            </AlertDescription>
          </Alert>
        )}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email address</FormLabel>
              <FormControl>
                <Input type="email" autoComplete="email" {...field} />
              </FormControl>
              <FormDescription>
                Used for notifications and account recovery. A new address must
                be confirmed through a verification mail.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={!isDirty || isSubmitting || !isOnline}
          >
            {isSubmitting && <Loader2 className="animate-spin" />}
            Send verification mail
          </Button>
        </div>
      </form>
    </Form>
  );
}
